/**
 * Document Reviewer Page
 *
 * THE MAIN HUMAN-IN-THE-LOOP INTERFACE
 *
 * This is the core component of the HITL workflow. It displays:
 * - Left pane (50%): Document viewer showing the original document
 * - Right pane (50%): Form with AI-extracted data for review
 *
 * Features:
 * - AI extraction on page load
 * - Confidence-based field highlighting (yellow = needs review)
 * - Side-by-side comparison
 * - Save functionality
 * - Navigation between multiple documents (prev/next arrows)
 *
 * Flow:
 * 1. Page receives files array from query params
 * 2. Triggers AI extraction for current file
 * 3. Displays document alongside extracted data
 * 4. User reviews, edits if needed, then saves
 * 5. User can navigate to next/prev document
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate, useBlocker } from 'react-router-dom';
import { usePageContext } from '../context/PageContext';
import DocumentViewer from '../components/documents/DocumentViewer';
import QueueDetailDrawer from '../components/documents/QueueDetailDrawer';
import POForm from '../components/purchase-orders/POForm';
import OBForm from '../components/ocean-booking/OBForm';
import BLForm from '../components/bill-of-lading/BLForm';
import FileListItem, { EXTRACTION_STATUS } from '../components/purchase-orders/FileListItem';
import Button from '../components/common/Button';
import StatusBadge from '../components/common/StatusBadge';
import ConfidenceBar from '../components/common/ConfidenceBar';
import ActionsDropdown from '../components/common/ActionsDropdown';
import { formatDate } from '../utils/formatDate';
import { getOverallScore, getUnifiedIssues, getWorstSeverity } from '../utils/confidenceUtils';
import SparklesIcon from '../components/common/SparklesIcon';
import * as extractionApi from '../api/extractionApi';
import * as purchaseOrderApi from '../api/purchaseOrderApi';
import * as queueApi from '../api/queueApi';
import { buildProxyUrl } from '../api/fileApi';

/**
 * DocumentReviewer Component
 */
function DocumentReviewer() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const formRef = useRef(null);
  const { setPageContext, clearPageContext } = usePageContext();

  // Parse files from URL params (supports multiple formats)
  // - queueId: Load from document queue (already extracted)
  // - files: JSON array of file objects
  // - objectKey + mimeType: Single file (legacy)
  const getFilesFromParams = useCallback(() => {
    const filesParam = searchParams.get('files');
    const singleObjectKey = searchParams.get('objectKey');
    const singleMimeType = searchParams.get('mimeType');
    const singleSignedUrl = searchParams.get('signedUrl');

    if (filesParam) {
      try {
        return JSON.parse(decodeURIComponent(filesParam));
      } catch {
        return [];
      }
    } else if (singleObjectKey && singleMimeType) {
      // Single file upload
      return [{
        objectKey: singleObjectKey,
        mimeType: singleMimeType,
        signedUrl: singleSignedUrl,
        originalName: singleObjectKey.split('/').pop()
      }];
    }
    // Note: queueId case is handled separately in useEffect
    return [];
  }, [searchParams]);

  // Check if we're loading from queue
  const queueId = searchParams.get('queueId');
  const [loadingFromQueue, setLoadingFromQueue] = useState(!!queueId);

  // Files array and current index
  const [files, setFiles] = useState(getFilesFromParams);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Per-file state tracking (keyed by objectKey)
  const [extractedDataMap, setExtractedDataMap] = useState({}); // objectKey -> data
  const [confidenceMap, setConfidenceMap] = useState({});       // objectKey -> confidence (legacy)
  const [evaluationMap, setEvaluationMap] = useState({});       // objectKey -> evaluation (LLM-as-judge)
  const [savedStatus, setSavedStatus] = useState({});           // objectKey -> boolean
  const [extractionStatus, setExtractionStatus] = useState({}); // objectKey -> EXTRACTION_STATUS

  // Current file state
  const [documentUrl, setDocumentUrl] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  // Drawer state (now used for agent activity timeline)
  const [isDrawerOpen, setIsDrawerOpen] = useState(true);

  // Agent activity timeline state
  const [processingLog, setProcessingLog] = useState([]);

  // Queue entry metadata for detail panel (keyed by objectKey)
  const [queueEntryMap, setQueueEntryMap] = useState({});

  // Navigation blocking state
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);

  // Check if there are unsaved files (extracted but not saved)
  const hasUnsavedWork = files.some(file => {
    const isExtracted = extractionStatus[file.objectKey] === EXTRACTION_STATUS.DONE ||
      extractedDataMap[file.objectKey];
    const isSaved = savedStatus[file.objectKey];
    return isExtracted && !isSaved;
  });

  // Block navigation when there's unsaved work
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      hasUnsavedWork && currentLocation.pathname !== nextLocation.pathname
  );

  // Handle blocker state changes
  useEffect(() => {
    if (blocker.state === 'blocked') {
      if (!hasUnsavedWork) {
        blocker.proceed?.();
        setShowLeaveConfirm(false);
      } else {
        setShowLeaveConfirm(true);
      }
    }
  }, [blocker, hasUnsavedWork]);

  // Add beforeunload listener for browser refresh/close
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedWork) {
        e.preventDefault();
        e.returnValue = ''; // Chrome requires returnValue to be set
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedWork]);

  /**
   * Effect to load queue entries when queueId is provided.
   * Loads ALL extracted documents from the queue so the user can
   * navigate between them with prev/next arrows.  The clicked
   * document (queueId) becomes the initial current index.
   */
  useEffect(() => {
    if (!queueId) return;

    const loadQueueEntries = async () => {
      try {
        setLoadingFromQueue(true);

        // Fetch all extracted documents from the queue
        const listResponse = await queueApi.getQueue({ status: 'extracted', limit: 100 });
        const allExtracted = listResponse.data.documents || [];

        // Build file objects and pre-populate state maps for every entry
        const fileList = [];
        const dataMap = {};
        const confMap = {};
        const evalMap = {};
        const statusMap = {};
        const entryMap = {};

        for (const entry of allExtracted) {
          fileList.push({
            queueId: entry._id,
            objectKey: entry.objectKey,
            mimeType: entry.mimeType,
            originalName: entry.originalFilename,
            status: entry.status,
            detectedFileType: entry.detectedFileType
          });

          entryMap[entry.objectKey] = entry;

          if (entry.extractedData) {
            dataMap[entry.objectKey] = entry.extractedData;
            confMap[entry.objectKey] = entry.confidence || {};
            evalMap[entry.objectKey] = entry.evaluation || null;

            statusMap[entry.objectKey] = EXTRACTION_STATUS.DONE;
          }
        }

        // If the clicked queueId isn't in the extracted list (edge case),
        // fetch it individually and prepend it.
        let startIndex = fileList.findIndex(f => f.queueId === queueId);
        if (startIndex === -1) {
          const singleResponse = await queueApi.getQueueEntry(queueId);
          const entry = singleResponse.data;
          const file = {
            queueId: entry._id,
            objectKey: entry.objectKey,
            mimeType: entry.mimeType,
            originalName: entry.originalFilename,
            status: entry.status,
            detectedFileType: entry.detectedFileType
          };
          fileList.unshift(file);
          entryMap[entry.objectKey] = entry;
          if (entry.extractedData) {
            dataMap[entry.objectKey] = entry.extractedData;
            confMap[entry.objectKey] = entry.confidence || {};
            evalMap[entry.objectKey] = entry.evaluation || null;

            statusMap[entry.objectKey] = EXTRACTION_STATUS.DONE;
          }
          startIndex = 0;
        }

        setFiles(fileList);
        setCurrentIndex(startIndex);
        setExtractedDataMap(dataMap);
        setConfidenceMap(confMap);
        setEvaluationMap(evalMap);
        setExtractionStatus(statusMap);
        setQueueEntryMap(entryMap);

        // Load processing log for the initially selected entry
        const initialEntry = allExtracted[startIndex];
        if (initialEntry?.processingLog) {
          setProcessingLog(initialEntry.processingLog);
        }
      } catch (err) {
        console.error('Failed to load queue entries:', err);
        setError(err.message || 'Failed to load documents from queue');
      } finally {
        setLoadingFromQueue(false);
      }
    };

    loadQueueEntries();
  }, [queueId]);

  // Current file
  const currentFile = files[currentIndex];
  const totalFiles = files.length;
  const hasMultipleFiles = totalFiles > 1;
  const currentObjectKey = currentFile?.objectKey;

  // Check if current file is extracting
  const isExtracting = currentFile && extractionStatus[currentFile.objectKey] === EXTRACTION_STATUS.EXTRACTING;

  // Track files currently being processed to prevent duplicate extractions
  const processingRef = useRef(new Set());

  /**
   * Extract data for a specific file via async queue-based flow.
   * 1. Triggers extraction (sets queue entry to 'pending')
   * 2. Polls queue entry every 2s until status is 'extracted' or 'failed'
   * 3. Reads extracted data from the queue entry
   */
  const extractFileData = useCallback(async (file) => {
    const { queueId: fileQueueId, objectKey } = file;

    if (!fileQueueId) return;

    // Skip if already being processed
    if (processingRef.current.has(objectKey)) return;
    processingRef.current.add(objectKey);

    // Mark as extracting in state
    setExtractionStatus(prev => {
      if (prev[objectKey] === EXTRACTION_STATUS.EXTRACTING || prev[objectKey] === EXTRACTION_STATUS.DONE) {
        processingRef.current.delete(objectKey);
        return prev;
      }
      return { ...prev, [objectKey]: EXTRACTION_STATUS.EXTRACTING };
    });

    try {
      // Trigger extraction — resets queue entry to 'pending' for agent pickup
      await extractionApi.extractDocument({ queueId: fileQueueId });

      // Poll queue entry until extraction completes
      const maxAttempts = 120; // 4 minutes at 2s intervals
      for (let i = 0; i < maxAttempts; i++) {
        await new Promise(resolve => setTimeout(resolve, 2000));

        const pollResponse = await queueApi.getQueueEntry(fileQueueId);
        const entry = pollResponse.data;

        // Update processing log in real time
        if (entry.processingLog) {
          setProcessingLog(prev => {
            // Only update if this is the current file
            return entry.processingLog;
          });
        }

        if (entry.status === 'extracted' && entry.extractedData) {
          setExtractedDataMap(prev => ({ ...prev, [objectKey]: entry.extractedData }));
          setConfidenceMap(prev => ({ ...prev, [objectKey]: entry.confidence || {} }));
          setEvaluationMap(prev => ({ ...prev, [objectKey]: entry.evaluation || null }));
          setExtractionStatus(prev => ({ ...prev, [objectKey]: EXTRACTION_STATUS.DONE }));
          setQueueEntryMap(prev => ({ ...prev, [objectKey]: entry }));
          break;
        }

        if (entry.status === 'failed') {
          setError(entry.error || 'Extraction failed');
          setExtractionStatus(prev => ({ ...prev, [objectKey]: EXTRACTION_STATUS.ERROR }));
          break;
        }

        // If still pending/processing, keep polling
      }
    } catch (err) {
      console.error(`Failed to extract ${objectKey}:`, err);
      setExtractionStatus(prev => ({ ...prev, [objectKey]: EXTRACTION_STATUS.ERROR }));
    } finally {
      processingRef.current.delete(objectKey);
    }
  }, []); // No dependencies needed - uses refs and functional updates

  /**
   * Retry extraction for current file
   */
  const retryExtraction = useCallback(() => {
    if (currentFile) {
      setError(null);
      // Clear from processing ref to allow retry
      processingRef.current.delete(currentFile.objectKey);
      setExtractionStatus(prev => ({
        ...prev,
        [currentFile.objectKey]: EXTRACTION_STATUS.PENDING
      }));
      extractFileData(currentFile);
    }
  }, [currentFile, extractFileData]);

  /**
   * Delete current file from the review list (queue-sourced POs only)
   */
  const handleDelete = async () => {
    if (!currentFile?.queueId) return;
    if (!window.confirm(`Delete "${currentFile?.originalName}"? This will permanently remove the document from the queue.`)) return;

    try {
      setIsSaving(true);
      await queueApi.deleteQueueEntry(currentFile.queueId);

      const remainingFiles = files.filter((_, idx) => idx !== currentIndex);
      if (remainingFiles.length === 0) {
        navigate('/document-queue', { state: { message: 'Document deleted.' } });
      } else {
        const newIndex = Math.min(currentIndex, remainingFiles.length - 1);
        setFiles(remainingFiles);
        setCurrentIndex(newIndex);
      }
    } catch (err) {
      setError(`Delete failed: ${err.response?.data?.error || err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Reject current file (mark as rejected via queue endpoint)
   */
  const handleReject = async () => {
    if (!currentFile?.queueId) return;
    const notes = window.prompt('Rejection reason (optional):');
    if (notes === null) return; // user cancelled

    try {
      setIsSaving(true);
      await queueApi.rejectDocument(currentFile.queueId, notes || '', false);

      // Mark as rejected so UI shows it's been actioned
      setSavedStatus(prev => ({ ...prev, [currentFile.objectKey]: 'rejected' }));

      // Remove the rejected document from the files list so navigation stays accurate
      const remainingFiles = files.filter((f, idx) => idx !== currentIndex);

      if (remainingFiles.length === 0) {
        navigate('/document-queue', { state: { message: 'Document rejected.' } });
      } else {
        const newIndex = Math.min(currentIndex, remainingFiles.length - 1);
        setFiles(remainingFiles);
        setCurrentIndex(newIndex);
      }
    } catch (err) {
      setError(`Rejection failed: ${err.response?.data?.error || err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Reprocess current file — triggers re-extraction and navigates back to the queue
   * with an informational banner so the user can monitor progress there.
   */
  const handleReprocess = async () => {
    if (!currentFile?.queueId) return;
    try {
      await extractionApi.extractDocument({ queueId: currentFile.queueId });
      const filename = currentFile.originalName || currentFile.filename || 'Document';
      navigate('/document-queue', {
        state: { message: `${filename} is being reprocessed` },
      });
    } catch (err) {
      setError(`Reprocess failed: ${err.response?.data?.error || err.message}`);
    }
  };

  /**
   * Effect to start background extraction for all files on mount
   * Skips if loading from queue with pre-extracted data
   */
  useEffect(() => {
    // Don't run if loading from queue (handled by queue entry effect)
    if (loadingFromQueue || queueId) return;
    if (files.length === 0) return;

    // Initialize extraction status for all files
    const initialStatus = {};
    files.forEach(file => {
      initialStatus[file.objectKey] = EXTRACTION_STATUS.PENDING;
    });
    setExtractionStatus(initialStatus);

    // Start extracting the first file immediately
    extractFileData(files[0]);

    // Start background extraction for remaining files with a slight delay
    // to prioritize the first file
    if (files.length > 1) {
      const timeoutId = setTimeout(() => {
        files.slice(1).forEach((file, index) => {
          // Stagger the requests slightly to avoid overwhelming the server
          setTimeout(() => {
            extractFileData(file);
          }, index * 500); // 500ms delay between each background extraction
        });
      }, 1000); // Wait 1 second before starting background extractions

      return () => clearTimeout(timeoutId);
    }
  }, [loadingFromQueue, queueId, files, extractFileData]); // Include all dependencies

  /**
   * Effect to update document URL when current file changes
   * Uses proxy URL to avoid CORS issues with MinIO signed URLs
   */
  useEffect(() => {
    if (currentFile) {
      // Use proxy URL through backend to avoid CORS issues
      setDocumentUrl(buildProxyUrl(currentFile.objectKey));
      setError(null);
    }
  }, [currentIndex, currentFile]);

  /**
   * Effect to load processing log when current file changes.
   * Only applies to queue-sourced files (files with a queueId).
   * Direct-upload files have no processing log — they use inline extraction.
   * Polls every 2s while the document status is 'processing' to give
   * the timeline a real-time feel, then stops once extraction completes.
   */
  useEffect(() => {
    // Direct-upload files don't have a queueId and thus no processing log.
    // Clear the timeline and bail out early.
    if (!currentFile?.queueId) {
      setProcessingLog([]);
      return;
    }

    let cancelled = false;
    let intervalId = null;

    const fetchLog = async () => {
      try {
        const response = await queueApi.getQueueEntry(currentFile.queueId);
        if (cancelled) return;
        const entry = response.data;
        setProcessingLog(entry.processingLog || []);

        // Once the document finishes processing (extracted/failed/approved),
        // stop polling — there won't be any more log entries.
        if (entry.status !== 'processing' && intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
      } catch {
        // Network errors are non-fatal — the timeline just freezes
        // until the next successful poll. No user-facing error needed
        // because the timeline is supplementary, not primary UI.
      }
    };

    // Always fetch once to populate the timeline immediately
    fetchLog();

    // Only poll while the orchestrator is actively writing log entries.
    // Once status leaves 'processing', fetchLog will auto-stop the interval.
    if (currentFile.status === 'processing') {
      intervalId = setInterval(fetchLog, 2000);
    }

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, [currentFile?.queueId, currentFile?.status]);

  // Publish page context so the ChatWidget can send it to the agent.
  // Includes what document is being reviewed, key extracted fields, and issues.
  // Dependencies use stable map references + objectKey rather than derived
  // variables (currentIssues, currentConfidence) which are new objects each render.
  useEffect(() => {
    if (!currentObjectKey) return;
    const data = extractedDataMap[currentObjectKey];
    if (!data) return;

    const evaluation = evaluationMap[currentObjectKey] || null;
    const confidence = confidenceMap[currentObjectKey] || {};
    const queueEntry = queueEntryMap[currentObjectKey];
    const saved = savedStatus[currentObjectKey];
    const issues = getUnifiedIssues(queueEntry, evaluation);

    const qEntry = queueEntry;
    const docType = qEntry?.documentType || 'purchase_order';
    const isOB = docType === 'ocean_booking';

    const poNumber = data.header?.poNumber;
    const bookingRef = data.bookingRequestNumber || data.header?.bookingRequestNumber;
    const parties = (data.parties || []).map(p => ({
      type: p.type,
      name: p.name,
    }));
    const itemCount = isOB ? (data.requestedEquipment?.length || 0) : (data.items?.length || 0);
    const overallScore = getOverallScore(evaluation, confidence);

    setPageContext({
      page: 'Document Review',
      documentType: docType,
      poNumber: isOB ? undefined : (poNumber || '(not yet extracted)'),
      bookingRef: isOB ? (bookingRef || '(not yet extracted)') : undefined,
      parties,
      itemCount,
      overallScore,
      issueCount: issues.length,
      issues: issues.slice(0, 10).map(i => ({
        field: i.field,
        type: i.type,
        severity: i.severity,
        detail: i.detail,
      })),
      status: saved === 'rejected' ? 'rejected'
        : saved ? 'approved'
        : 'under review',
      filename: currentFile?.originalName || currentFile?.filename,
    });

    return () => clearPageContext();
  }, [currentObjectKey, extractedDataMap, evaluationMap, confidenceMap, queueEntryMap, savedStatus, currentFile, setPageContext, clearPageContext]);

  /**
   * Navigate to previous PO
   */
  const goToPrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setError(null);
    }
  };

  /**
   * Navigate to next PO
   */
  const goToNext = () => {
    if (currentIndex < totalFiles - 1) {
      setCurrentIndex(currentIndex + 1);
      setError(null);
    }
  };

  /**
   * Remove a file from the list
   * @param {number} indexToRemove - Index of file to remove
   */
  const removeFile = (indexToRemove) => {
    const fileToRemove = files[indexToRemove];

    // Don't allow removing if it's already saved
    if (savedStatus[fileToRemove.objectKey]) {
      return;
    }

    // Remove the file
    const newFiles = files.filter((_, idx) => idx !== indexToRemove);

    // If no files left, navigate back to upload page
    if (newFiles.length === 0) {
      navigate('/purchase-orders/ingest');
      return;
    }

    // Adjust current index if needed
    let newIndex = currentIndex;
    if (indexToRemove < currentIndex) {
      // Removed file was before current, shift index down
      newIndex = currentIndex - 1;
    } else if (indexToRemove === currentIndex) {
      // Removed current file, stay at same index or go to last if at end
      newIndex = Math.min(currentIndex, newFiles.length - 1);
    }

    // Clean up state for removed file
    setExtractedDataMap(prev => {
      const newMap = { ...prev };
      delete newMap[fileToRemove.objectKey];
      return newMap;
    });
    setConfidenceMap(prev => {
      const newMap = { ...prev };
      delete newMap[fileToRemove.objectKey];
      return newMap;
    });
    setEvaluationMap(prev => {
      const newMap = { ...prev };
      delete newMap[fileToRemove.objectKey];
      return newMap;
    });
    setExtractionStatus(prev => {
      const newMap = { ...prev };
      delete newMap[fileToRemove.objectKey];
      return newMap;
    });

    setFiles(newFiles);
    setCurrentIndex(newIndex);
  };

  // Determine document type from the queue entry for the current file
  const currentDocumentType = queueEntryMap[currentFile?.objectKey]?.documentType || 'purchase_order';
  const isOceanBooking = currentDocumentType === 'ocean_booking';
  const isOceanBillOfLading = currentDocumentType === 'ocean_bill_of_lading';

  /**
   * Handle form submission (Save)
   * @param {Object} formData - Validated form data
   */
  const handleSubmit = async (formData) => {
    setIsSaving(true);
    setError(null);

    try {
      // Check if this came from the document queue
      if (currentFile.queueId) {
        // Build the correct request body key based on document type
        const approvalData = isOceanBillOfLading
          ? { blData: formData }
          : isOceanBooking
            ? { obData: formData }
            : { poData: formData };

        // Use queue approval endpoint - it handles PO/OB/BL creation and file moving
        const result = await queueApi.approveDocument(currentFile.queueId, approvalData);

        // Mark as saved
        setSavedStatus(prev => ({
          ...prev,
          [currentFile.objectKey]: true
        }));

        // Remove the approved document from the files list so navigation stays accurate
        const remainingFiles = files.filter((f, idx) => idx !== currentIndex);

        if (remainingFiles.length === 0) {
          // All done — navigate to the appropriate detail or list page
          if (isOceanBillOfLading) {
            navigate(`/bills-of-lading/${result.data.billOfLading._id}`);
          } else if (isOceanBooking) {
            navigate(`/ocean-bookings/${result.data.oceanBooking._id}`);
          } else {
            navigate(`/purchase-orders/${result.data.purchaseOrder._id}`);
          }
        } else {
          // More documents remain — stay in reviewer and advance index
          const newIndex = Math.min(currentIndex, remainingFiles.length - 1);
          setFiles(remainingFiles);
          setCurrentIndex(newIndex);
        }
      } else {
        // Direct upload - use the standard PO creation API
        await purchaseOrderApi.createPurchaseOrder({
          ...formData,
          status: 'ACTIVE',
          documentObjectKey: currentFile.objectKey,
          extractionConfidence: confidenceMap[currentFile.objectKey]
        });

        // Mark as saved
        setSavedStatus(prev => ({
          ...prev,
          [currentFile.objectKey]: true
        }));

        // Remove the approved document from the files list so navigation stays accurate
        const remainingFiles = files.filter((f, idx) => idx !== currentIndex);

        if (remainingFiles.length === 0) {
          navigate('/purchase-orders', { state: { message: 'Purchase order created successfully!' } });
        } else {
          const newIndex = Math.min(currentIndex, remainingFiles.length - 1);
          setFiles(remainingFiles);
          setCurrentIndex(newIndex);
        }
      }
    } catch (err) {
      const docLabel = isOceanBillOfLading ? 'bill of lading' : isOceanBooking ? 'ocean booking' : 'purchase order';
      setError(err.message || `Failed to save ${docLabel}.`);
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Count saved POs
   */
  const savedCount = Object.values(savedStatus).filter(Boolean).length;

  /**
   * Count extracted POs (for background progress)
   */
  const extractedCount = Object.values(extractionStatus).filter(
    status => status === EXTRACTION_STATUS.DONE
  ).length;
  const extractingCount = Object.values(extractionStatus).filter(
    status => status === EXTRACTION_STATUS.EXTRACTING
  ).length;

  // Loading state when loading from queue
  if (loadingFromQueue) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-8rem)]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-lg text-gray-600">
            Loading document...
          </p>
        </div>
      </div>
    );
  }

  // No files error state
  if (files.length === 0) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-8rem)]">
        <div className="text-center max-w-md">
          <svg
            className="mx-auto h-16 w-16 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <h2 className="mt-4 text-xl font-medium text-gray-900">
            No Documents Found
          </h2>
          <p className="mt-2 text-gray-600">
            Please upload documents first.
          </p>
          <button
            onClick={() => navigate('/purchase-orders/ingest')}
            className="mt-4 text-blue-600 hover:underline"
          >
            Upload Documents
          </button>
        </div>
      </div>
    );
  }

  // Loading state during extraction
  if (isExtracting) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-8rem)]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-lg text-gray-600">
            Extracting data from document...
          </p>
          <p className="mt-2 text-sm text-gray-500">
            {hasMultipleFiles ? `Document ${currentIndex + 1} of ${totalFiles}` : 'This may take a few seconds'}
          </p>
          <p className="mt-2 text-sm font-medium text-orange-600">
            Using Anthropic Claude
          </p>
          {hasMultipleFiles && extractingCount > 1 && (
            <p className="mt-2 text-sm text-blue-500">
              Also extracting {extractingCount - 1} other document{extractingCount > 2 ? 's' : ''} in the background
            </p>
          )}
        </div>
      </div>
    );
  }

  // Error state (only if extraction failed for current file)
  if (extractionStatus[currentFile?.objectKey] === EXTRACTION_STATUS.ERROR && !extractedDataMap[currentFile?.objectKey]) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-8rem)]">
        <div className="text-center max-w-md">
          <svg
            className="mx-auto h-16 w-16 text-red-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <h2 className="mt-4 text-xl font-medium text-gray-900">
            Extraction Failed
          </h2>
          <p className="mt-2 text-gray-600">{error}</p>
          <div className="mt-4 space-x-4">
            <button
              onClick={retryExtraction}
              className="text-blue-600 hover:underline"
            >
              Try Again
            </button>
            {hasMultipleFiles && currentIndex < totalFiles - 1 && (
              <>
                <span className="text-gray-400">|</span>
                <button
                  onClick={goToNext}
                  className="text-blue-600 hover:underline"
                >
                  Skip to Next
                </button>
              </>
            )}
            <span className="text-gray-400">|</span>
            <button
              onClick={() => navigate('/purchase-orders/ingest')}
              className="text-blue-600 hover:underline"
            >
              Upload New Document
            </button>
          </div>
        </div>
      </div>
    );
  }

  const currentExtractedData = extractedDataMap[currentFile?.objectKey];
  const currentConfidence = confidenceMap[currentFile?.objectKey] || {};
  const currentEvaluation = evaluationMap[currentFile?.objectKey] || null;
  const isCurrentSaved = savedStatus[currentFile?.objectKey];
  const currentIssues = getUnifiedIssues(queueEntryMap[currentFile?.objectKey], currentEvaluation);

  return (
    <div className="h-[calc(100vh-10rem)] flex flex-col overflow-hidden">
      {/* Page Header with Navigation */}
      <div className="flex-shrink-0 pb-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          {/* Left side: Title, filename, then action buttons */}
          <div className="flex items-center gap-3 min-w-0">
            <h1 className="text-2xl font-bold text-gray-900 flex-shrink-0">
              Review Document
            </h1>
            <span className="text-gray-400 flex-shrink-0">|</span>
            <span className="text-sm text-gray-500 flex-shrink-0">
              {isOceanBillOfLading ? 'Bill of Lading' : isOceanBooking ? 'Ocean Booking' : 'Purchase Order'}
            </span>
            <span className="text-gray-400 flex-shrink-0">|</span>
            <span className="text-gray-600 truncate max-w-xs">
              {currentFile?.originalName || currentFile?.filename}
            </span>

            {/* Evaluation / confidence score bar */}
            {getOverallScore(currentEvaluation, currentConfidence) != null && (
              <>
                <span className="text-gray-300 flex-shrink-0">|</span>
                <ConfidenceBar value={getOverallScore(currentEvaluation, currentConfidence)} variant="compact" />
              </>
            )}

          </div>

          {/* Right side: Save, Navigation, Drawer toggle */}
          <div className="flex items-center gap-2">
            {/* Save Button in Header */}
            {!isCurrentSaved && (
              <>
                <Button
                  variant="secondary"
                  onClick={handleReject}
                  loading={isSaving}
                  className="!text-red-600 !border-red-200 hover:!bg-red-50 hover:!border-red-300"
                >
                  Reject
                </Button>
                <Button
                  variant="success"
                  onClick={() => formRef.current?.submit()}
                  loading={isSaving}
                >
                  Approve
                </Button>
              </>
            )}

            {/* Navigation Arrows */}
            {hasMultipleFiles && (
              <div className="flex items-center space-x-2 ml-2">
                <Button
                  variant="secondary"
                  onClick={goToPrevious}
                  disabled={currentIndex === 0}
                  className="!px-3"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </Button>

                <span className="text-sm text-gray-600 min-w-[80px] text-center">
                  {currentIndex + 1} / {totalFiles}
                </span>

                <Button
                  variant="secondary"
                  onClick={goToNext}
                  disabled={currentIndex === totalFiles - 1}
                  className="!px-3"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Button>
              </div>
            )}

            {/* Separator before AI Details */}
            <div className="h-6 w-px bg-gray-300"></div>

            {/* Toggle agent activity panel */}
            {(() => {
              const drawerIssues = getUnifiedIssues(queueEntryMap[currentFile?.objectKey], currentEvaluation);
              const drawerSeverity = getWorstSeverity(drawerIssues);
              const drawerDotColor = {
                error: 'bg-red-500',
                warning: 'bg-amber-500',
                info: 'bg-blue-500',
              }[drawerSeverity];
              return (
                <button
                  onClick={() => setIsDrawerOpen(!isDrawerOpen)}
                  className={`relative p-2 rounded-md border transition-colors ${isDrawerOpen
                    ? 'bg-blue-50 border-blue-200 text-blue-600'
                    : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
                    }`}
                  title={isDrawerOpen ? 'Hide AI details' : 'Show AI details'}
                >
                  <SparklesIcon className="w-4 h-4" />
                  {drawerDotColor && (
                    <span className={`absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full ring-2 ring-white ${drawerDotColor}`} />
                  )}
                </button>
              );
            })()}

            {/* Three-dot actions menu (Delete + Reprocess) */}
            {currentFile?.queueId && !isCurrentSaved && (
              <ActionsDropdown
                actions={[
                  {
                    label: 'Reprocess',
                    icon: (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    ),
                    onClick: handleReprocess,
                    hidden: extractionStatus[currentFile.objectKey] === EXTRACTION_STATUS.EXTRACTING,
                  },
                  {
                    label: 'Delete',
                    variant: 'danger',
                    icon: (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    ),
                    onClick: handleDelete,
                  },
                ]}
              />
            )}
          </div>
        </div>
      </div>

      {/* Already actioned indicator */}
      {isCurrentSaved && isCurrentSaved === 'rejected' && (
        <div className="flex-shrink-0 mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <svg className="h-5 w-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            <p className="ml-2 text-sm text-red-700">
              This document has been rejected.
              {hasMultipleFiles && currentIndex < totalFiles - 1 && (
                <button onClick={goToNext} className="ml-2 underline">
                  Go to next →
                </button>
              )}
            </p>
          </div>
        </div>
      )}
      {isCurrentSaved && isCurrentSaved !== 'rejected' && (
        <div className="flex-shrink-0 mt-4 bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <svg className="h-5 w-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <p className="ml-2 text-sm text-green-700">
              This {isOceanBillOfLading ? 'bill of lading' : isOceanBooking ? 'ocean booking' : 'purchase order'} has been approved.
              {hasMultipleFiles && currentIndex < totalFiles - 1 && (
                <button onClick={goToNext} className="ml-2 underline">
                  Go to next →
                </button>
              )}
            </p>
          </div>
        </div>
      )}

      {/* Error banner (for save errors) */}
      {error && currentExtractedData && (
        <div className="flex-shrink-0 mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <svg
              className="h-5 w-5 text-red-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p className="ml-2 text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Split Pane Container */}
      <div className="flex-1 flex mt-4 min-h-0 min-w-0 gap-2">
        {/* Left Pane - Document Viewer */}
        <div className="w-1/2 border border-gray-200 rounded-lg overflow-hidden min-w-0">
          <div className="bg-gray-100 px-4 py-2 border-b border-gray-200">
            <h2 className="text-sm font-medium text-gray-700">
              Original Document
            </h2>
          </div>
          <div className="h-[calc(100%-2.5rem)] overflow-hidden">
            <DocumentViewer
              url={documentUrl}
              mimeType={currentFile?.mimeType}
              detectedFileType={currentFile?.detectedFileType}
              filename={currentFile?.originalName}
              objectKey={currentFile?.objectKey}
            />
          </div>
        </div>

        {/* Right Pane - Form */}
        <div className="w-1/2 border border-gray-200 rounded-lg overflow-hidden min-w-0">
          <div className="bg-gray-100 px-4 py-2 border-b border-gray-200 flex justify-between items-center">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-medium text-gray-700">
                Extracted Data (Editable)
              </h2>
              <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-orange-100 text-orange-700">
                Anthropic Claude
              </span>
            </div>
            {isCurrentSaved && isCurrentSaved === 'rejected' && (
              <span className="text-xs text-red-600 font-medium">Rejected</span>
            )}
            {isCurrentSaved && isCurrentSaved !== 'rejected' && (
              <span className="text-xs text-green-600 font-medium">Approved</span>
            )}
          </div>
          <div className="h-[calc(100%-2.5rem)] overflow-auto p-4">
            {isOceanBillOfLading ? (
              <BLForm
                ref={formRef}
                key={`${currentFile?.objectKey}-${extractionStatus[currentFile?.objectKey]}`}
                initialData={currentExtractedData}
                evaluation={currentEvaluation}
                issues={currentIssues}
                onSubmit={handleSubmit}
                isSubmitting={isSaving}
                submitLabel="Approve"
                disabled={isCurrentSaved}
                hideActions={true}
              />
            ) : isOceanBooking ? (
              <OBForm
                ref={formRef}
                key={`${currentFile?.objectKey}-${extractionStatus[currentFile?.objectKey]}`}
                initialData={currentExtractedData}
                evaluation={currentEvaluation}
                issues={currentIssues}
                onSubmit={handleSubmit}
                isSubmitting={isSaving}
                submitLabel="Approve"
                disabled={isCurrentSaved}
                hideActions={true}
              />
            ) : (
              <POForm
                ref={formRef}
                key={`${currentFile?.objectKey}-${extractionStatus[currentFile?.objectKey]}`}
                initialData={currentExtractedData}
                evaluation={currentEvaluation}
                confidence={currentConfidence}
                issues={currentIssues}
                onSubmit={handleSubmit}
                isSubmitting={isSaving}
                submitLabel="Approve"
                disabled={isCurrentSaved}
                hideActions={true}
              />
            )}
          </div>
        </div>
      </div>

      {/* Overlay drawer for document details + extraction pipeline */}
      <QueueDetailDrawer
        open={isDrawerOpen}
        queueEntry={queueEntryMap[currentFile?.objectKey]}
        evaluation={currentEvaluation}
        confidence={currentConfidence}
        filename={currentFile?.originalName || currentFile?.filename}
        processingLog={processingLog}
        onClose={() => setIsDrawerOpen(false)}
      />

      {/* Navigation Warning Modal */}
      {showLeaveConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                Unsaved Documents
              </h3>
            </div>

            <p className="text-gray-600 mb-2">
              You have {files.filter(f => {
                const isExtracted = extractionStatus[f.objectKey] === EXTRACTION_STATUS.DONE || extractedDataMap[f.objectKey];
                return isExtracted && !savedStatus[f.objectKey];
              }).length} unsaved document(s).
            </p>
            <p className="text-gray-600 mb-6">
              If you leave this page, all unsaved data will be lost. Are you sure you want to leave?
            </p>

            <div className="flex justify-end gap-3">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowLeaveConfirm(false);
                  blocker.reset?.();
                }}
              >
                Stay on Page
              </Button>
              <Button
                variant="danger"
                onClick={() => {
                  setShowLeaveConfirm(false);
                  blocker.proceed?.();
                }}
              >
                Leave Page
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DocumentReviewer;
